<template>
  <div class="order-management">
    <FlashMessage ref="flashMessage" />
    <LoadingIndicator v-if="loading" message="Loading orders..." />

    <!-- Delete confirmation modal -->
    <div v-if="showDeleteModal" class="modal-overlay">
      <div class="confirm-modal">
        <h3>Confirm Deletion</h3>
        <p>
          Are you sure you want to delete order <strong>{{ orderToDelete?.order_number }}</strong>? 
          This action cannot be undone.
        </p>
        <div class="confirm-buttons">
          <button @click="showDeleteModal = false" class="btn-secondary">
            Cancel
          </button>
          <button @click="deleteOrder" class="btn-danger">Delete</button>
        </div>
      </div>
    </div>

    <!-- Status Update Confirmation Modal -->
    <div v-if="showStatusConfirmModal" class="modal-overlay">
    <div class="confirm-modal">
        <h3>Confirm Status Update</h3>
        <p>
        Are you sure you want to change order #<strong>{{ currentOrderForStatus?.order_number }}</strong> status from 
        <strong>{{ getOrderStatusName(currentOrderForStatus?.order_status_id) }}</strong> to 
        <strong>{{ getOrderStatusName(tempStatusId) }}</strong>?
        </p>
        <div class="confirm-buttons">
        <button @click="closeStatusConfirmModal" class="btn-secondary">
            Cancel
        </button>
        <button @click="confirmStatusUpdate" class="btn-primary">
            Confirm Update
        </button>
        </div>
    </div>
    </div>

    <!-- Order Details Modal -->
    <div v-if="showOrderDetailsModal" class="modal-overlay">
      <div class="order-details-modal">
        <div class="modal-header">
          <h3>Order Details #{{ selectedOrder?.order_number }}</h3>
          <button @click="closeModal" class="close-btn">&times;</button>
        </div>
        <div class="modal-body">


          <!-- Order summary -->
<div class="order-summary">
  <div class="summary-item">
    <strong>Order ID:</strong> {{ selectedOrder?.id }}
  </div>
  <div class="summary-item">
    <strong>Date:</strong> {{ formatDate(selectedOrder?.created_at) }}
  </div>
  <div class="summary-item">
    <strong>Customer Name:</strong> {{ getCustomerName(selectedOrder) }}
  </div>
  <div class="summary-item">
    <strong>Customer Email:</strong> {{ getCustomerEmail(selectedOrder) }}
  </div>
  <div class="summary-item">
    <strong>Member Since:</strong> {{ getCustomerJoiningDate(selectedOrder) }}
  </div>
  <div class="summary-item">
    <strong>Customer Phone:</strong> {{ getCustomerPhone(selectedOrder) }}
  </div>
  <div class="summary-item">
    <strong>Total Amount:</strong> ${{ formatPrice(selectedOrder?.total_amount) }}
  </div>
  <div class="summary-item">
    <strong>Shipping Address:</strong> {{ selectedOrder?.shipping_address || 'N/A' }}
  </div>
  <div class="summary-item">
    <strong>Payment Status:</strong> 
    <span :class="`status-badge payment-${getPaymentStatusClass(selectedOrder?.payment_status_id)}`">
      {{ getPaymentStatusName(selectedOrder?.payment_status_id) }}
    </span>
  </div>
  <div class="summary-item">
    <strong>Order Status:</strong>
    <span :class="`status-badge order-${getOrderStatusClass(selectedOrder?.order_status_id)}`">
      {{ getOrderStatusName(selectedOrder?.order_status_id) }}
    </span>
  </div>
</div>

          <!-- Order items -->
          <h4>Order Items</h4>
          <table class="items-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>SKU</th>
                <th>Quantity</th>
                <th>Unit Price</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in selectedOrder?.items" :key="item.id">
                <td>{{ item.product_name }}</td>
                <td>{{ item.product_sku }}</td>
                <td>{{ item.quantity }}</td>
                <td>${{ formatPrice(item.price) }}</td>
                <td>${{ formatPrice(item.total) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="modal-footer">
          <button @click="closeModal" class="btn-secondary">Close</button>
        </div>
      </div>
    </div>

    <!-- Header -->
    <div class="header">
      <h1>Orders</h1>
    </div>

    <!-- Search and filter bar -->
    <div class="control-bar">
      <div class="search-box">
        <i class="fas fa-search"></i>
        <input
          type="text"
          v-model="searchQuery"
          placeholder="Search by order #, customer ID, product..."
          @input="applyFilters"
        />
      </div>

      <div class="filters">
        <select v-model="statusFilter" @change="applyFilters">
          <option value="">All Order Statuses</option>
          <option value="Pending">Pending</option>
          <option value="Processing">Processing</option>
          <option value="Completed">Completed</option>
          <option value="Cancelled">Cancelled</option>
          <option value="Shipped">Shipped</option>
          <option value="Delivered">Delivered</option>
        </select>

        <select v-model="paymentStatusFilter" @change="applyFilters">
          <option value="">All Payment Statuses</option>
          <option value="Pending">Pending</option>
          <option value="Paid">Paid</option>
          <option value="Failed">Failed</option>
          <option value="Refunded">Refunded</option>
        </select>

        <select v-model="minAmountFilter" @change="applyFilters">
          <option :value="null">Min Amount</option>
          <option :value="50">$50+</option>
          <option :value="100">$100+</option>
          <option :value="500">$500+</option>
          <option :value="1000">$1000+</option>
        </select>

        <button class="btn-secondary" @click="resetFilters">
          <i class="fas fa-sync-alt"></i> Reset
        </button>
      </div>
    </div>

    <!-- Orders table -->
    <div class="table-responsive">
      <table class="orders-table">
        <thead>
          <tr>
            <th @click="sortBy('id')">ID <i :class="sortIcon('id')"></i></th>
            <th @click="sortBy('order_number')">Order # <i :class="sortIcon('order_number')"></i></th>
            <th>Customer</th>
            <th @click="sortBy('total_amount')">Total Amount <i :class="sortIcon('total_amount')"></i></th>
            <th @click="sortBy('items_count')">Items <i :class="sortIcon('items_count')"></i></th>
            <th @click="sortBy('created_at')">Date <i :class="sortIcon('created_at')"></i></th>
            <th>Payment Status</th>
            <th>Order Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="order in filteredOrders" :key="order.id">
            <td class="order-id">{{ order.id }}</td>
            <td class="order-number">{{ order.order_number }}</td>

            <td>
            <div class="customer-info">
                <div class="customer-name">{{ getCustomerName(order) }}</div>
                <div class="customer-email">{{ getcustomerPhone(order) }}</div>
                <div class="customer-email">{{ getCustomerEmail(order) }}</div>
            </div>
            </td>


            <td class="amount">${{ formatPrice(order.total_amount) }}</td>
            <td class="text-center">{{ order.items?.length || 0 }}</td>
            <td>{{ formatDate(order.created_at) }}</td>
            <td>
              <span :class="`status-badge payment-${getPaymentStatusClass(order.payment_status_id)}`">
                {{ getPaymentStatusName(order.payment_status_id) }}
              </span>
            </td>
            <td>
              <div class="status-with-update">
                <span :class="`status-badge order-${getOrderStatusClass(order.order_status_id)}`">
                  {{ getOrderStatusName(order.order_status_id) }}
                </span>
                <button @click="openStatusUpdate(order)" class="btn-icon-small" title="Update Status">
                  <i class="fas fa-pen"></i>
                </button>
              </div>
            </td>
            <td class="actions">
              <button @click="viewOrder(order)" class="btn-icon" title="View Details">
                <i class="fas fa-eye"></i>
              </button>
              <!-- <button @click="editOrder(order)" class="btn-icon" title="Edit Order">
                <i class="fas fa-edit"></i>
              </button> -->
              <button @click="confirmDelete(order)" class="btn-icon" title="Delete Order">
                <i class="fas fa-trash"></i>
              </button>
            </td>
          </tr>
        </tbody>
       </table>
      
      <div v-if="filteredOrders.length === 0 && !loading" class="empty-state">
        <i class="fas fa-inbox"></i>
        <p>No orders found. Try adjusting your filters.</p>
      </div>
    </div>

    <!-- Pagination -->
    <div class="pagination" v-if="totalPages > 1">
      <button @click="currentPage--" :disabled="currentPage === 1" class="pagination-btn">
        <i class="fas fa-chevron-left"></i>
      </button>

      <button
        v-for="page in displayedPages"
        :key="page"
        @click="currentPage = page"
        :class="{ active: currentPage === page }"
        class="pagination-btn"
      >
        {{ page }}
      </button>

      <button @click="currentPage++" :disabled="currentPage === totalPages" class="pagination-btn">
        <i class="fas fa-chevron-right"></i>
      </button>
    </div>

    <!-- Export buttons -->
    <div class="export-actions" v-if="filteredOrders.length > 0">
      <button @click="exportToExcel(filteredOrders)" class="btn-export">
        <i class="fas fa-file-excel"></i> Excel
      </button>
      <button @click="printOrderReport(filteredOrders)" class="btn-export">
        <i class="fas fa-print"></i> Print
      </button>
      <button @click="exportToPDF(filteredOrders)" class="btn-export">
        <i class="fas fa-file-pdf"></i> PDF
      </button>
    </div>

    <!-- Status Update Dropdown (inline) -->
     <!-- we will later put actual values from the backend here -->
        <div v-if="showStatusDropdown" class="status-dropdown-overlay" @click="closeStatusDropdown">
        <div class="status-dropdown" @click.stop>
            <h4>Update Order Status</h4>
            <select v-model="tempStatusId">
            <option :value="1">Pending</option>
            <option :value="2">Processing</option>
            <option :value="3">Completed</option>
            <option :value="4">Cancelled</option>
            <option :value="5">Shipped</option>
            <option :value="6">Delivered</option>
            </select>
            <div class="dropdown-actions">
            <button @click="closeStatusDropdown" class="btn-secondary">Cancel</button>
            <button @click="submitStatusUpdate" class="btn-primary">Update</button>
            </div>
        </div>
        </div>

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import {
  fetchAllOrders,
} from "../../API-requests/Requests";
import FlashMessage from "@/components/FlashMessage.vue";
import LoadingIndicator from "@/components/Loading_indicator.vue";
import { excelParser } from "@/services/excel-parser";


const apiCategories = ref([]);
const filteredBrands = ref([]);
const flashMessage = ref(null);


//for status handling
const showStatusDropdown = ref(false);
const tempStatusId = ref(null);
const currentOrderForStatus = ref(null);
const showStatusConfirmModal = ref(false);


const formData = ref({
  name: "",
  description: "",
  price: 0,
  location: "",
  discount_price: 0,
  stock: 0,
  sku: "",
  thumbnail: "",
  product_images: [],
  category_id: null,
  brand_id: null,
  is_active: true,
});
// ============ REACTIVE STATE ============
// Modal states
const showAddModal = ref(false);
const showDeleteModal = ref(false);
const showOrderDetailsModal = ref(false);
const editingOrder = ref(null);
const orderToDelete = ref(null);
const selectedOrder = ref(null);

// Filter states
const searchQuery = ref("");
const statusFilter = ref("");
const paymentStatusFilter = ref("");
const minAmountFilter = ref(null);
const dateFromFilter = ref(null);
const dateToFilter = ref(null);

// Sort states
const sortField = ref("id");
const sortDirection = ref("asc");

// Pagination states
const currentPage = ref(1);
const itemsPerPage = ref(10);

// Data states
const orders = ref([]);
const loading = ref(false);

// ============ HELPER FUNCTIONS ============
const formatPrice = (price) => {
  return parseFloat(price).toFixed(2);
};

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleDateString();
};

const getOrderStatusName = (statusId) => {
  const statusMap = {
    1: "Pending",
    2: "Processing",
    3: "Completed",
    4: "Cancelled",
    5: "Shipped",
    6: "Delivered",
  };
  return statusMap[statusId] || "Unknown";
};

const getOrderStatusClass = (statusId) => {
  const classMap = {
    1: "pending",
    2: "processing",
    3: "completed",
    4: "cancelled",
    5: "shipped",
    6: "delivered",
  };
  return classMap[statusId] || "unknown";
};

const getPaymentStatusName = (statusId) => {
  const statusMap = {
    1: "Pending",
    2: "Paid",
    3: "Failed",
    4: "Refunded",
    5: "Partial",
  };
  return statusMap[statusId] || "Unknown";
};

const getPaymentStatusClass = (statusId) => {
  const classMap = {
    1: "pending",
    2: "paid",
    3: "failed",
    4: "refunded",
    5: "partial",
  };
  return classMap[statusId] || "unknown";
};

const getCustomerName = (order) => {
  if (order.user) {
    // Return full name if both first and last name exist
    if (order.user.first_name && order.user.last_name) {
      return `${order.user.first_name} ${order.user.last_name}`;
    }
    // Return first name if only that exists
    if (order.user.first_name) {
      return order.user.first_name;
    }
    // Return email as fallback
    if (order.user.email) {
      return order.user.email;
    }
  }
  // Ultimate fallback
  return `Customer #${order.user_id}`;
};


// Get customer email
const getCustomerEmail = (order) => {
  return order.user?.email || 'N/A';
};

//get customer phone number
const getcustomerPhone = (order) => {
  return order.user?.phone || 'N/A';
};

// Get customer phone
const getCustomerPhone = (order) => {
  return order.user?.phone || 'N/A';
};

//customer joing date
const getCustomerJoiningDate = (order) => {
  if (!order.user?.created_at) return 'N/A';
  
  const date = new Date(order.user.created_at);
  const options = { year: 'numeric', month: 'short', day: 'numeric' };
  return date.toLocaleDateString('en-US', options);
};


// Get customer full info for tooltip or details modal
const getCustomerInfo = (order) => {
  if (!order.user) return null;
  return {
    name: `${order.user.first_name || ''} ${order.user.last_name || ''}`.trim(),
    email: order.user.email,
    phone: order.user.phone,
    id: order.user.id
  };
};

// ============ COMPUTED PROPERTIES ============
const filteredOrders = computed(() => {
  let result = [...orders.value];

  // Apply search
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    result = result.filter(
      (order) =>
        order.order_number?.toLowerCase().includes(query) ||
        order.id.toString().includes(query) ||
        order.user_id?.toString().includes(query) ||
        order.shipping_address?.toLowerCase().includes(query) ||
        order.items?.some((item) =>
          item.product_name?.toLowerCase().includes(query)
        )
    );
  }

  // Filter by order status
  if (statusFilter.value) {
    result = result.filter(
      (order) => getOrderStatusName(order.order_status_id) === statusFilter.value
    );
  }

  // Filter by payment status
  if (paymentStatusFilter.value) {
    result = result.filter(
      (order) => getPaymentStatusName(order.payment_status_id) === paymentStatusFilter.value
    );
  }

  // Filter by minimum total amount
  if (minAmountFilter.value) {
    result = result.filter(
      (order) => parseFloat(order.total_amount) >= minAmountFilter.value
    );
  }

  // Filter by date range
  if (dateFromFilter.value) {
    result = result.filter(
      (order) => new Date(order.created_at) >= new Date(dateFromFilter.value)
    );
  }
  if (dateToFilter.value) {
    result = result.filter(
      (order) => new Date(order.created_at) <= new Date(dateToFilter.value)
    );
  }

  // Apply sorting
  result.sort((a, b) => {
    let aVal = a[sortField.value];
    let bVal = b[sortField.value];

    if (sortField.value === "created_at") {
      aVal = new Date(aVal);
      bVal = new Date(bVal);
    }

    if (sortField.value === "total_amount") {
      aVal = parseFloat(aVal);
      bVal = parseFloat(bVal);
    }

    if (sortField.value === "items_count") {
      aVal = a.items?.length || 0;
      bVal = b.items?.length || 0;
    }

    if (aVal < bVal) return sortDirection.value === "asc" ? -1 : 1;
    if (aVal > bVal) return sortDirection.value === "asc" ? 1 : -1;
    return 0;
  });

  // Apply pagination
  const start = (currentPage.value - 1) * itemsPerPage.value;
  return result.slice(start, start + itemsPerPage.value);
});

const totalPages = computed(() => {
  return Math.ceil(orders.value.length / itemsPerPage.value);
});

const totalOrders = computed(() => orders.value.length);
const totalRevenue = computed(() => {
  return orders.value.reduce((sum, order) => sum + parseFloat(order.total_amount), 0);
});

// ============ SORTING ============
const sortIcon = (field) => {
  if (sortField.value !== field) return "fas fa-sort";
  return sortDirection.value === "asc" ? "fas fa-sort-up" : "fas fa-sort-down";
};

const sortBy = (field) => {
  if (sortField.value === field) {
    sortDirection.value = sortDirection.value === "asc" ? "desc" : "asc";
  } else {
    sortField.value = field;
    sortDirection.value = "asc";
  }
};

// ============ FILTERS ============
const applyFilters = () => {
  currentPage.value = 1;
};

const resetFilters = () => {
  searchQuery.value = "";
  statusFilter.value = "";
  paymentStatusFilter.value = "";
  minAmountFilter.value = null;
  dateFromFilter.value = null;
  dateToFilter.value = null;
  sortField.value = "id";
  sortDirection.value = "asc";
  currentPage.value = 1;
};

// ============ DATA LOADING ============
const loadOrders = async () => {
  try {
    loading.value = true;
    const orderData = await fetchAllOrders();

    orders.value = orderData.map((order) => ({
      id: order.id,
      order_number: order.order_number,
      user_id: order.user_id,
      total_amount: order.total_amount,
      payment_method_id: order.payment_method_id,
      payment_status_id: order.payment_status_id,
      order_status_id: order.order_status_id,
      shipping_address: order.shipping_address,
      created_at: order.created_at,
      updated_at: order.updated_at,
      archive: order.archive,
      user: order.user || null,

      // Map the items (products in this order)
      items: order.items?.map((item) => ({
        id: item.id,
        product_id: item.product_id,
        quantity: item.quantity,
        price: item.price,
        total: item.total,
        product_name: item.product?.name || "Unknown Product",
        product_sku: item.product?.sku || "N/A",
        product_stock: item.product?.stock || 0,
        product_price: parseFloat(
          item.product?.discount_price && item.product?.discount_price !== "0.00"
            ? item.product.discount_price
            : item.product?.price || 0
        ),
        thumbnail: item.product?.thumbnail || null,
      })) || [],
    }));
  } catch (error) {
    console.error("Error loading orders:", error);
    // Show error message to user
  } finally {
    loading.value = false;
  }
};

// ============ ORDER ACTIONS ============
const viewOrder = (order) => {
  selectedOrder.value = order;
  showOrderDetailsModal.value = true;
};

const editOrder = (order) => {
  editingOrder.value = order;
  showAddModal.value = true;
};

const confirmDelete = (order) => {
  orderToDelete.value = order;
  showDeleteModal.value = true;
};

const deleteOrder = async () => {
  try {
    loading.value = true;
    await apiDeleteOrder(orderToDelete.value.id);
    orders.value = orders.value.filter((o) => o.id !== orderToDelete.value.id);
    // Show success message
  } catch (error) {
    console.error("Error deleting order:", error);
    // Show error message
  } finally {
    loading.value = false;
    showDeleteModal.value = false;
    orderToDelete.value = null;
  }
};

// const updateOrderStatus = async (order, newStatusId) => {
//   try {
//     loading.value = true;
//     await apiUpdateOrderStatus(order.id, newStatusId);
//     const index = orders.value.findIndex((o) => o.id === order.id);
//     if (index !== -1) {
//       orders.value[index].order_status_id = newStatusId;
//     }
//     // Show success message
//   } catch (error) {
//     console.error("Error updating order status:", error);
//     // Show error message
//   } finally {
//     loading.value = false;
//   }
// };

// ============ MODAL HANDLERS ============
const closeModal = () => {
  showAddModal.value = false;
  showOrderDetailsModal.value = false;
  editingOrder.value = null;
  selectedOrder.value = null;
};




// Status update methods
const openStatusUpdate = (order) => {
  currentOrderForStatus.value = order;
  tempStatusId.value = order.order_status_id;
  showStatusDropdown.value = true;
};

const closeStatusDropdown = () => {
  showStatusDropdown.value = false;
  currentOrderForStatus.value = null;
  tempStatusId.value = null;
  showStatusDropdown.value = false;
};

const submitStatusUpdate = () => {
  // Close the dropdown and open confirmation modal
  showStatusDropdown.value = false;
  showStatusConfirmModal.value = true;
};

const closeStatusConfirmModal = () => {
  showStatusConfirmModal.value = false;
};


const confirmStatusUpdate = async () => {
  try {
    loading.value = true;
    
    // Call your API function here
    // await apiUpdateOrderStatus(currentOrderForStatus.value.id, tempStatusId.value);
    
    // For now, just show alert
    alert(`Order status updated successfully! Order #${currentOrderForStatus.value?.order_number} status changed to ${getOrderStatusName(tempStatusId.value)}`);
    
    // Update local data
    const index = orders.value.findIndex(o => o.id === currentOrderForStatus.value.id);
    if (index !== -1) {
      orders.value[index].order_status_id = tempStatusId.value;
    }
    
    // Close modals
    closeStatusConfirmModal();
    currentOrderForStatus.value = null;
    tempStatusId.value = null;
    
  } catch (error) {
    console.error("Error updating order status:", error);
    alert("Failed to update order status. Please try again.");
  } finally {
    loading.value = false;
  }
};

const updateOrderStatus = async () => {
  // Show confirmation dialog
  const confirmed = confirm(
    `Are you sure you want to change order #${currentOrderForStatus.value?.order_number} status from "${getOrderStatusName(currentOrderForStatus.value?.order_status_id)}" to "${getOrderStatusName(tempStatusId.value)}"?`
  );
  
  if (!confirmed) {
    closeStatusDropdown();
    return;
  }
  
  try {
    loading.value = true;
    
    // Call your API function here
    // await apiUpdateOrderStatus(currentOrderForStatus.value.id, tempStatusId.value);
    
    // For now, just show alert
    alert(`Order status updated successfully! Order #${currentOrderForStatus.value?.order_number} status changed to ${getOrderStatusName(tempStatusId.value)}`);
    
    // Update local data
    const index = orders.value.findIndex(o => o.id === currentOrderForStatus.value.id);
    if (index !== -1) {
      orders.value[index].order_status_id = tempStatusId.value;
    }
    
    // Close dropdown
    closeStatusDropdown();
    
  } catch (error) {
    console.error("Error updating order status:", error);
    alert("Failed to update order status. Please try again.");
  } finally {
    loading.value = false;
  }
};


const exportToExcel = (data) => {
  if (!data || data.length === 0) {
    alert('No data available to export!');
    return;
  }

  try {
    const parser = excelParser();
    const fileName = `order-export-${new Date().toISOString().slice(0, 19)}`;
    // Use the TRUE Excel function - no warnings!
    parser.exportToTrueExcel(data, fileName, 'order report');
  } catch (error) {
    console.error('Export failed:', error);
    alert('Failed to export Excel file!');
  }
}

const exportToPDF = (data) => {
  if (!data || data.length === 0) {
    alert('No data available to export!');
    return;
  }

  try {
    const parser = excelParser();
    const fileName = `payment-methods-export-${new Date().toISOString().slice(0, 19)}`;
    
    // Call the PDF export function from your service
    parser.exportToPDF(data, fileName, {
      companyName: 'EKU MARKET',
      title: 'Order Report',
      theme: 'striped',  // Options: 'striped', 'grid', 'plain'
      headerColor: [41, 128, 185],  // Blue header
      fontSize: 10
    });
  } catch (error) {
    console.error('PDF Export failed:', error);
    alert('Failed to export PDF!');
  }
}



const printOrderReport = (data) => {
  if (!data || data.length === 0) {
    alert('No data available to print!');
    return;
  }
  try {
    const parser = excelParser();
    
    parser.printData(data, {
      title: 'Order Report',
      companyName: 'EKU MARKET',
      headerBgColor: '#4CAF50',
      autoPrint: true, 
      footer: 'Confidential Document'
    });
  } catch (error) {
    console.error('Print failed:', error);
    alert('Failed to print data!');
  }
}


// ============ LIFEHOOK ============
onMounted(() => {
  loadOrders();
});

</script>

<style scoped>


.modal-overlay {
  position: fixed; /* Ensure the modal overlays on top */
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5); /* Semi-transparent background */
  z-index: 9999; /* Ensure it appears on top of all other content */
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem;
}

.confirm-modal {
  background: white;
  padding: 2rem;
  border-radius: 8px;
  max-width: 500px;
  width: 90%;
  text-align: center;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1); /* Add a subtle shadow for depth */
  animation: fadeIn 0.3s ease-out; /* Smooth fade-in effect */
}

.confirm-buttons {
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 1.5rem;
}

.confirm-buttons button {
  padding: 0.8rem 1.5rem;
  font-size: 1rem;
  border-radius: 5px;
  cursor: pointer;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .form-grid {
    grid-template-columns: 1fr;
  }

  .form-group.full-width {
    grid-column: span 1;
  }

  .header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }

  .control-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .filters {
    flex-direction: column;
    align-items: stretch;
  }

  .orders-table th,
  .orders-table td {
    padding: 0.75rem 0.5rem;
    font-size: 0.9rem;
  }

  .actions {
    flex-wrap: wrap;
  }
}

@media (max-width: 576px) {
  .product-management {
    padding: 1rem;
  }

  .modal-content {
    width: 95%;
  }

  .modal-footer {
    flex-direction: column;
  }

  .modal-footer button {
    width: 100%;
  }
}

/* Add export button styles */
.export-actions {
  display: flex;
  gap: 0.75rem;
  margin-top: 1rem;
  flex-wrap: wrap;
}

.btn-export {
  padding: 0.5rem 1rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: white;
  color: #333;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s;
}

.btn-export:hover {
  background-color: #f5f5f5;
}

.btn-export i {
  font-size: 1rem;
}














/* Order Details Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.order-details-modal {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6b7280;
  transition: color 0.2s;
}

.close-btn:hover {
  color: #111827;
}

.modal-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
}

.modal-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
}

/* Order Summary */
.order-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
  padding: 1rem;
  background: #f9fafb;
  border-radius: 8px;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem;
}

.summary-item strong {
  color: #4b5563;
  font-weight: 600;
}

/* Items Table */
.items-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
  background: white;
}

.items-table thead {
  background: #f3f4f6;
}

.items-table th {
  padding: 0.75rem 1rem;
  text-align: left;
  font-weight: 600;
  color: #374151;
  border-bottom: 2px solid #e5e7eb;
}

.items-table td {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #e5e7eb;
  color: #4b5563;
}

.items-table tbody tr:hover {
  background: #f9fafb;
}

/* Modal body content spacing */
.modal-body h4 {
  font-size: 1rem;
  font-weight: 600;
  margin: 1.5rem 0 1rem 0;
  color: #111827;
}

.modal-body h4:first-of-type {
  margin-top: 0;
}

/* Status Badges */
.status-badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: capitalize;
}

/* Order Status Badges */
.order-pending {
  background: #fef3c7;
  color: #92400e;
}

.order-processing {
  background: #dbeafe;
  color: #1e40af;
}

.order-completed {
  background: #d1fae5;
  color: #065f46;
}

.order-cancelled {
  background: #fee2e2;
  color: #991b1b;
}

.order-shipped {
  background: #e0e7ff;
  color: #3730a3;
}

.order-delivered {
  background: #a7f3d0;
  color: #064e3b;
}

/* Payment Status Badges */
.payment-pending {
  background: #fef3c7;
  color: #92400e;
}

.payment-paid {
  background: #d1fae5;
  color: #065f46;
}

.payment-failed {
  background: #fee2e2;
  color: #991b1b;
}

.payment-refunded {
  background: #f3e8ff;
  color: #6b21a5;
}

.payment-partial {
  background: #fed7aa;
  color: #9a3412;
}

/* Status with update button */
.status-with-update {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-icon-small {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  color: #6b7280;
  transition: color 0.2s;
}

.btn-icon-small:hover {
  color: #3b82f6;
}

/* Status Dropdown */
.status-dropdown-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
}

.status-dropdown {
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  min-width: 300px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
}

.status-dropdown h4 {
  margin: 0 0 1rem 0;
  font-size: 1rem;
  font-weight: 600;
}

.status-dropdown select {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  margin-bottom: 1rem;
}

.dropdown-actions {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
}

/* Statistics Cards */
.stats-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
}

.stat-card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.stat-icon {
  width: 48px;
  height: 48px;
  background: #3b82f6;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
}

.stat-info h3 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: #111827;
}

.stat-info p {
  margin: 0.25rem 0 0 0;
  color: #6b7280;
  font-size: 0.875rem;
}

/* Order Management Container */
.order-management {
  padding: 2rem;
  background: #f3f4f6;
  min-height: 100vh;
}

/* Table Styles */
.table-responsive {
  overflow-x: auto;
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.orders-table {
  width: 100%;
  border-collapse: collapse;
}

.orders-table thead {
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.orders-table th {
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
  user-select: none;
  transition: background 0.2s;
}

.orders-table th:hover {
  background: #f3f4f6;
}

.orders-table td {
  padding: 1rem;
  border-bottom: 1px solid #f3f4f6;
  color: #4b5563;
}

.orders-table tbody tr:hover {
  background: #f9fafb;
}

.order-number {
  font-family: monospace;
  font-weight: 500;
}

.amount {
  font-weight: 600;
  color: #111827;
}

.text-center {
  text-align: center;
}

/* Action Buttons */
.actions {
  display: flex;
  gap: 0.5rem;
}

.btn-icon {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 6px;
  transition: all 0.2s;
  color: #6b7280;
}

.btn-icon:hover {
  background: #f3f4f6;
  color: #3b82f6;
}

.btn-icon.danger:hover {
  color: #ef4444;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 3rem;
  color: #6b7280;
}

.empty-state i {
  font-size: 3rem;
  margin-bottom: 1rem;
  color: #d1d5db;
}

.empty-state p {
  margin: 0;
}

/* Control Bar */
.control-bar {
  background: white;
  padding: 1rem;
  border-radius: 12px;
  margin-bottom: 1.5rem;
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  align-items: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.search-box {
  flex: 1;
  min-width: 200px;
  position: relative;
}

.search-box i {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
}

.search-box input {
  width: 100%;
  padding: 0.5rem 0.5rem 0.5rem 2rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
}

.filters {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.filters select,
.filters button {
  padding: 0.5rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
  cursor: pointer;
}

/* Pagination */
.pagination {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 1.5rem;
  flex-wrap: wrap;
}

.pagination-btn {
  padding: 0.5rem 1rem;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.pagination-btn:hover:not(:disabled) {
  background: #f3f4f6;
  border-color: #9ca3af;
}

.pagination-btn.active {
  background: #3b82f6;
  color: white;
  border-color: #3b82f6;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Export Actions */
.export-actions {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  margin-top: 1.5rem;
}

.btn-export {
  padding: 0.5rem 1rem;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-export:hover {
  background: #f3f4f6;
  border-color: #9ca3af;
}

/* Header */
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.header h1 {
  margin: 0;
  font-size: 1.875rem;
  font-weight: 700;
  color: #111827;
}

.btn-primary {
  background: #3b82f6;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: background 0.2s;
}

.btn-primary:hover {
  background: #2563eb;
}

.btn-secondary {
  background: #6b7280;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-secondary:hover {
  background: #4b5563;
}

.btn-danger {
  background: #ef4444;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-danger:hover {
  background: #dc2626;
}

/* Confirm Modal */
.confirm-modal {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  width: 90%;
  max-width: 400px;
}

.confirm-modal h3 {
  margin: 0 0 1rem 0;
}

.confirm-buttons {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  margin-top: 1.5rem;
}
</style>
